import { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';
import { trackConsent } from '../lib/analytics';

const CONSENT_KEY = 'pniel_cookie_consent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(CONSENT_KEY)) return undefined;
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add('cookie-visible');
    return () => document.body.classList.remove('cookie-visible');
  }, [visible]);

  const accept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    if (typeof window.loadAnalytics === 'function') window.loadAnalytics();
    trackConsent('accept');
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    trackConsent('decline');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-24 sm:bottom-4 left-4 z-40 max-w-[350px] w-[calc(100%-2rem)] sm:w-auto bg-white rounded-2xl shadow-2xl border border-gray-200 p-4"
      style={{ animation: 'cookieIn 0.4s cubic-bezier(0.16,1,0.3,1)' }}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center">
          <Cookie className="w-5 h-5 text-[#003A70]" />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-700 leading-relaxed">
            We use cookies for analytics to improve your experience. We only track you if you accept. See our{' '}
            <a href="/privacy" data-testid="cookie-privacy-link" className="text-[#003A70] font-semibold hover:underline">
              Privacy Policy
            </a>.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <button
              data-testid="cookie-accept-btn"
              onClick={accept}
              className="px-4 py-2 rounded-lg bg-[#003A70] text-white text-sm font-semibold hover:bg-[#1e40af] transition-colors"
            >
              Accept
            </button>
            <button
              data-testid="cookie-dismiss-btn"
              onClick={decline}
              className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Decline
            </button>
          </div>
        </div>
        <button onClick={decline} aria-label="Close cookie notice" className="text-gray-300 hover:text-gray-500 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      <style>{`@keyframes cookieIn{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
